import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Award, Shield, CheckCircle, Leaf, Settings, Lock, Building, Star, BadgeCheck } from 'lucide-react'

const certifications = [
  {
    name: 'ISO 9001:2015 IAF-3',
    title: 'Quality Management System',
    description: 'Certified for maintaining highest quality standards in all products and services.',
    icon: Award,
    color: '#6366f1',
  },
  {
    name: 'ISO 14001:2015 IAF',
    title: 'Environmental Management',
    description: 'Committed to sustainable practices and environmental responsibility in all operations.',
    icon: Leaf,
    color: '#10b981',
  },
  {
    name: 'ISO 45001:2018 IAF',
    title: 'Occupational Health & Safety',
    description: 'Ensuring safe and healthy working conditions for all employees and stakeholders.',
    icon: Shield,
    color: '#f59e0b',
  },
  {
    name: 'ISO 50001',
    title: 'Energy Management',
    description: 'Systematic approach to energy management for improved efficiency and sustainability.',
    icon: Settings,
    color: '#ec4899',
  },
  {
    name: 'ISO 20000-1:2018',
    title: 'IT Service Management',
    description: 'Internationally recognized standard for IT service management excellence.',
    icon: CheckCircle,
    color: '#8b5cf6',
  },
  {
    name: 'ISO 27001:2013',
    title: 'Information Security',
    description: 'Robust information security controls to protect sensitive data and systems.',
    icon: Lock,
    color: '#06b6d4',
  },
  {
    name: 'AIOTA',
    title: 'Industry Association',
    description: 'Member of All India Organisation of Technical Education for industry standards.',
    icon: Building,
    color: '#f97316',
  },
  {
    name: 'BIFMA',
    title: 'Business Furniture Standards',
    description: 'Certified for meeting international furniture safety and performance standards.',
    icon: Star,
    color: '#84cc16',
  },
  {
    name: 'Green Pro',
    title: 'Eco-Friendly Products',
    description: 'Certified for environmentally responsible product manufacturing and sourcing.',
    icon: Leaf,
    color: '#22c55e',
  },
  {
    name: 'Green Guard',
    title: 'Environmental Certification',
    description: 'Meeting strict environmental standards for indoor air quality and sustainability.',
    icon: BadgeCheck,
    color: '#14b8a6',
  },
]

const Certifications = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })

  return (
    <>
      <Helmet>
        <title>Certifications | Eonixa Dynamics</title>
        <meta name="description" content="ISO 9001:2015 IAF-3, ISO 14001:2015 IAF, ISO 45001:2018 IAF, ISO 50001, ISO 20000-1:2018, ISO 27001:2013, AIOTA, BIFMA, Green Pro, Green Guard Certified" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[50vh] overflow-hidden pt-24">
        <div className="container-enterprise relative z-10 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.25)',
              }}
            >
              <Award className="w-4 h-4 text-primary" />
              <span className="text-sm text-slate-300">Our Certifications</span>
            </motion.div>
            <h1 className="mb-6 text-4xl font-bold text-white sm:text-5xl">
              ISO <span className="gradient-text">Certifications</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              Our commitment to excellence is backed by internationally recognized certifications. We maintain the highest standards in Quality, Security, Environment, and Service Management.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Certifications Grid */}
      <section ref={ref} className="py-24 relative overflow-hidden">
        <div className="container-enterprise relative z-10">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {certifications.map((cert, index) => {
              const Icon = cert.icon
              return (
                <motion.div
                  key={cert.name}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="group relative"
                >
                  <div className="rounded-2xl p-6 h-full"
                    style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{
                          background: `${cert.color}20`,
                          border: `1px solid ${cert.color}40`,
                        }}
                      >
                        <Icon className="w-6 h-6" style={{ color: cert.color }} />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-white">{cert.name}</h3>
                        <p className="text-sm" style={{ color: cert.color }}>{cert.title}</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-400 leading-relaxed text-justify">{cert.description}</p>
                  </div>
                </motion.div>
              )
            })}
          </div>

          {/* Commitment Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="mt-16"
          >
            <div className="rounded-2xl p-8 text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.2)',
              }}
            >
              <h2 className="mb-4 text-2xl font-bold text-white">Our Commitment</h2>
              <p className="text-slate-400 max-w-3xl mx-auto">
                These certifications reflect our unwavering commitment to quality, security, and continuous improvement.
                They are not just badges on our wall, but principles we live by every day in serving our clients and partners.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default Certifications